import { SignalRContext } from "../../App";
import { FC, Props } from "./typings";
import { INotification } from "dto/INotification";
import { ENotificationType, notification } from "utils/notification";
import { axiosInstance } from "api/axios";
import { useContext } from "react";
import KeysContext from "contexts/KeysContext";
import PackagesContext from "contexts/PackagesContext";
import CasesContext from "contexts/CasesContext";
import { useNavigate } from "react-router-dom";
import chatService from "api/chatService";
import { IKey } from "dto/IKey";
import { decryptPackage } from "utils/decryptPackage";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { removeHtml } from "utils/parseHTML";
import { useTranslation } from "react-i18next";
import { renderNotificationMessage } from "utils/notificationMessage";

const NotificationManager: FC<Props> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { keys, setKeys } = useContext(KeysContext);
  const { packages, setPackages } = useContext(PackagesContext);
  const { cases, setCases } = useContext(CasesContext);
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const { mutateAsync: getChatAsync } = chatService.getChatAsync();

  const getCase = async (id: string, kind: string) => {
    const caseData = await axiosInstance.get(`/case?id=${id}&kind=${kind}`);
    const newKeys = [caseData.data.Keys].flat();
    setKeys([...keys, newKeys].flat());
    setCases([caseData.data, ...cases]);
  };

  const getPackage = async (id: string, kind: string) => {
    // jw. — bez id/kind leciało /list?Id=undefined&Kind=undefined -> 400 (Sentry #289).
    if (!id || !kind) return;
    try {
      const packageData = await axiosInstance.get(
        `/list?Id=${id}&Kind=${kind}`,
      );

      const newKeys = [
        packageData.data.ObjectKeys,
        packageData.data.Keys,
      ].flat();
      packageData.data.Items[0].Unread = 1;
      setKeys([...keys, newKeys].flat());
      setPackages([packageData.data.Items[0], ...packages].flat());
    } catch (e) {
      console.warn("[NotificationManager] getPackage failed", id, kind, e);
    }
  };

  SignalRContext.useSignalREffect(
    "notification",
    (message: INotification) => {
      notification({
        type: ENotificationType.INFO,
        title: message.label,
        text: renderNotificationMessage(message),
      });

      if (message.reason === 9) {
        return;
      }

      if (message.associatedObjectKind === 1024) {
        getCase(message.associatedObjectId, message.associatedObjectKind);
        return;
      }

      getPackage(message.associatedObjectId, message.associatedObjectKind);
    },
    [],
  );

  const showChatNotification = async (m: any, key: IKey) => {
    const content = await decryptPackage(userId, m.content, key, null);

    notification({
      type: ENotificationType.INFO,
      title: t("chat.newMessage"),
      text: `${t("chat.from")}: ${m.senderLabel}\r\n${removeHtml(content)}`,
      callback: () => {
        navigate(`/chat/${m.chatId}`);
      },
    });
  };

  SignalRContext.useSignalREffect(
    "newChatMessage",
    (message) => {
      const encryptionKey = keys.find((x) => x.Id === message.encryptionKeyId);

      if (!encryptionKey) {
        getChatAsync(message.chatId).then((r) => {
          setKeys([...keys, r.data.EncryptionKey]);
          showChatNotification(message, r.data.EncryptionKey);
        });
      } else {
        showChatNotification(message, encryptionKey);
      }
    },
    [],
  );

  SignalRContext.useSignalREffect(
    "outgoingParcelState",
    (message: any) => {
      const newPacks = [...packages];

      const pack = newPacks.find((p) => p.Id === message.id);

      if (pack) {
        pack.state = message.status;
      }

      setPackages(newPacks);
    },
    [],
  );

  SignalRContext.useSignalREffect(
    "newCase",
    (message: any) => {
      setCases([
        {
          Id: message.id,
          Kind: message.kind,
          CreatedByUserId: message.createdByUserId,
          CreationTimestamp: message.creationTimestamp,
          ProceedingId: message.proceedingId,
          Signature: message.signature,
          State: message.state,
          Title: message.title,
          Unread: message.unread,
          HasNotifications: false,
          Size: message.size,
          LastAction: message.lastAction,
          IsShared: message.isShared,
          IsRemoved: message.isRemoved,
          RelationsCount: message.relationsCount,
          SubstitutionsCount: message.substitutionsCount,
          Result: message.result,
          DraftId: message.draftId,
          IsDeskActive: message.isDeskActive,
          TagIds: message.tagIds,
          FromPI: message.fromPi,
          Court: message.cout,
          Client: message.client,
          LeadBy: message.leadBy,
          Participants: [],
          Keys: [],
          PartialShare: message.partialShare,
          Privileges: message.privileges,
        },
        ...cases,
      ]);
    },
    [],
  );
  return <></>;
};

export default NotificationManager;
